// Vuex module managing per-conversation translation prefs + in-memory caches.
// Persisted to localStorage via `preferences.js`.
import {
  loadPrefs,
  savePrefs,
  DEFAULT_PREFS,
  SELF_LANGUAGE,
} from '../preferences';
import {
  translateInboundMessages,
  translateOutboundDraft,
} from '../api';
import logger from '../logger';

// Resolve current account id with multiple fallbacks so the action works
// regardless of vuex-router-sync state shape across Chatwoot versions.
const resolveAccountId = rootState => {
  const fromRoute = rootState?.route?.params?.accountId;
  if (fromRoute) return fromRoute;
  const fromAccounts = rootState?.accounts?.currentAccountId;
  if (fromAccounts) return fromAccounts;
  const fromActiveAccount = rootState?.userActiveAccount?.id;
  if (fromActiveAccount) return fromActiveAccount;
  if (typeof window !== 'undefined') {
    // Prefer public account identifiers (alphanumeric), fall back to numeric.
    const match = window.location.pathname.match(
      /\/accounts\/([a-z0-9][a-z0-9_-]*)/i
    );
    if (match) return match[1];
  }
  return null;
};

// Module-local (non-reactive) registry to dedupe in-flight requests.
// Lives outside Vuex state so we don't break strict-mode invariants.
const inFlightMessageIds = new Set();

// Micro-batch queue: coalesce many single-message dispatch calls (e.g. when
// many message bubbles mount at once) into ONE LLM HTTP request.
const BATCH_DEBOUNCE_MS = 200;
const pendingQueues = new Map(); // conversationId -> { ids: Set, accountId, sourceLang, direction, timer }

const state = () => ({
  // prefs per conversation id => { inboundOn, outboundPreviewOn, peerLang }
  prefs: {},
  // translations cache (mirror of message.translations on the server side)
  // shape: { [messageId]: translatedString }
  inboundCache: {},
  // outbound preview cache: { conversationId: { source, translated, lang } }
  outboundPreview: {},
  isPreviewing: false,
  // map conversationId -> boolean: a batch translation is currently in-flight.
  // Used to disable the toggle/switches so rapid clicks don't queue duplicate work.
  inFlightConversations: {},
});

const getters = {
  prefsFor: state => conversationId => state.prefs[conversationId] || { ...DEFAULT_PREFS },
  translationFor: state => messageId => state.inboundCache[messageId],
  outboundPreviewFor: state => conversationId => state.outboundPreview[conversationId],
  isTranslatingConversation: state => conversationId => !!state.inFlightConversations[conversationId],
};

const mutations = {
  SET_PREFS(state, { conversationId, prefs }) {
    state.prefs = { ...state.prefs, [conversationId]: prefs };
  },
  MERGE_INBOUND_TRANSLATIONS(state, translations) {
    state.inboundCache = { ...state.inboundCache, ...translations };
  },
  SET_OUTBOUND_PREVIEW(state, { conversationId, payload }) {
    state.outboundPreview = {
      ...state.outboundPreview,
      [conversationId]: payload,
    };
  },
  CLEAR_OUTBOUND_PREVIEW(state, conversationId) {
    const next = { ...state.outboundPreview };
    delete next[conversationId];
    state.outboundPreview = next;
  },
  SET_PREVIEWING(state, value) {
    state.isPreviewing = value;
  },
  SET_CONVERSATION_IN_FLIGHT(state, { conversationId, value }) {
    state.inFlightConversations = {
      ...state.inFlightConversations,
      [conversationId]: value,
    };
  },
};

const actions = {
  loadConversationPrefs({ commit, rootState }, conversationId) {
    const accountId = resolveAccountId(rootState);
    const prefs = loadPrefs(accountId, conversationId);
    commit('SET_PREFS', { conversationId, prefs });
    logger.info('prefs.loaded', {
      conversationId,
      inboundOn: prefs.inboundOn,
      outboundPreviewOn: prefs.outboundPreviewOn,
      peerLang: prefs.peerLang,
    });
    return prefs;
  },

  updateConversationPrefs(
    { commit, dispatch, rootState },
    { conversationId, patch }
  ) {
    const accountId = resolveAccountId(rootState);
    const current = loadPrefs(accountId, conversationId);
    const next = { ...current, ...patch };
    savePrefs(accountId, conversationId, next);
    commit('SET_PREFS', { conversationId, prefs: next });
    logger.info('prefs.updated', { conversationId, ...patch });

    // peerLang is preview-only: invalidate stale draft preview, never re-batch bubbles.
    if (Object.prototype.hasOwnProperty.call(patch, 'peerLang')) {
      logger.info('prefs.peerLang.preview_only', {
        conversationId,
        peerLang: next.peerLang,
      });
      dispatch('clearOutboundPreview', conversationId);
    }
    return next;
  },

  // Public entry point used by components. Queues message ids and flushes
  // them in a single LLM HTTP request after BATCH_DEBOUNCE_MS of quiet time.
  // Bubble "help me read": source is always auto-detect; never peerLang.
  translateInbound({ dispatch }, { conversationId, messageIds }) {
    return dispatch('enqueueTranslateBatch', {
      conversationId,
      messageIds,
      sourceLang: 'auto',
      direction: 'in',
    });
  },

  translateOutbound({ dispatch }, { conversationId, messageIds }) {
    return dispatch('enqueueTranslateBatch', {
      conversationId,
      messageIds,
      sourceLang: 'auto',
      direction: 'out',
    });
  },

  enqueueTranslateBatch(
    { dispatch, state, rootState },
    { conversationId, messageIds, sourceLang, direction }
  ) {
    const accountId = resolveAccountId(rootState);
    if (!accountId) {
      logger.warn('translateBatch: missing accountId', { direction });
      return;
    }

    const filtered = messageIds.filter(
      id => !state.inboundCache[id] && !inFlightMessageIds.has(id)
    );
    if (filtered.length === 0) return;

    const queueKey = `${conversationId}:${direction}`;
    let queue = pendingQueues.get(queueKey);
    if (!queue) {
      queue = {
        ids: new Set(),
        accountId,
        sourceLang,
        direction,
        timer: null,
      };
      pendingQueues.set(queueKey, queue);
    }
    filtered.forEach(id => queue.ids.add(id));

    if (queue.timer) clearTimeout(queue.timer);
    queue.timer = setTimeout(() => {
      pendingQueues.delete(queueKey);
      dispatch('flushTranslateBatch', { conversationId, queue });
    }, BATCH_DEBOUNCE_MS);
  },

  async flushTranslateBatch({ commit }, { conversationId, queue }) {
    const ids = [...queue.ids];
    if (ids.length === 0) return;

    ids.forEach(id => inFlightMessageIds.add(id));
    commit('SET_CONVERSATION_IN_FLIGHT', { conversationId, value: true });
    logger.info('translateBatch: flushing', {
      conversationId,
      direction: queue.direction,
      sourceLang: queue.sourceLang || 'auto',
      targetLang: SELF_LANGUAGE,
      count: ids.length,
    });

    try {
      const data = await translateInboundMessages({
        accountId: queue.accountId,
        messageIds: ids,
        // Help-me-read bubbles: auto-detect source; never send peerLang here.
        sourceLang: queue.sourceLang || 'auto',
        targetLang: SELF_LANGUAGE,
        direction: queue.direction,
      });
      commit('MERGE_INBOUND_TRANSLATIONS', data.translations || {});
    } catch (err) {
      logger.error('translateBatch: failed', {
        direction: queue.direction,
        error: err?.message,
      });
    } finally {
      ids.forEach(id => inFlightMessageIds.delete(id));
      commit('SET_CONVERSATION_IN_FLIGHT', { conversationId, value: false });
    }
  },

  // Keep legacy name used by older callers during the debounce window.
  async flushInboundBatch({ dispatch }, { conversationId, queue }) {
    return dispatch('flushTranslateBatch', { conversationId, queue });
  },

  async previewOutbound(
    { commit, rootState },
    { conversationId, text, peerLang }
  ) {
    const accountId = resolveAccountId(rootState);
    if (!accountId) {
      logger.warn('previewOutbound: missing accountId');
      return null;
    }
    commit('SET_PREVIEWING', true);
    try {
      const data = await translateOutboundDraft({
        accountId,
        conversationId,
        text,
        targetLang: peerLang,
      });
      commit('SET_OUTBOUND_PREVIEW', {
        conversationId,
        payload: { source: text, translated: data.translated, lang: peerLang },
      });
      return data.translated;
    } finally {
      commit('SET_PREVIEWING', false);
    }
  },

  clearOutboundPreview({ commit }, conversationId) {
    commit('CLEAR_OUTBOUND_PREVIEW', conversationId);
  },

  // Collect conversation messages of the given direction and enqueue translate.
  batchTranslateConversation(
    { dispatch, rootGetters },
    { conversationId, direction }
  ) {
    const conversation =
      rootGetters.getConversationById?.(conversationId) ||
      rootGetters['conversations/getConversationById']?.(conversationId) ||
      null;
    const messages = conversation?.messages || [];
    const isOut = direction === 'out';
    const filtered = messages.filter(m => {
      if (!m?.content) return false;
      if (isOut) return Number(m.message_type) === 1 && !m.private;
      return Number(m.message_type) === 0;
    });

    if (!filtered.length) {
      logger.info('batchTranslateConversation: nothing to translate', {
        conversationId,
        direction,
      });
      return;
    }

    const action = isOut ? 'translateOutbound' : 'translateInbound';
    logger.info('batchTranslateConversation', {
      conversationId,
      direction,
      count: filtered.length,
    });
    dispatch(action, {
      conversationId,
      messageIds: filtered.map(m => m.id),
    });
  },
};

export default {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
};
